import React, { useEffect, useState } from "react";
import { Mail, Globe, MapPin, Phone, PhoneOutgoing, Building2, CalendarClock, MessageSquare, ExternalLink, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { PoseEagle } from "../components/PoseEagle";
import { api } from "../lib/api";
import { GHL_CALENDAR_URL, GHL_FORM_URL, externalLinkProps } from "../config/ghl";
import { SITE_INFO } from "../config/site";
import { Logo } from "../components/Logo";
import { Seo } from "../components/Seo";
import { CTASection } from "../components/sections/CTASection";
import { Band } from "../components/Band";

const telHref = (raw) => "tel:" + String(raw || "").replace(/[^\d+]/g, "");

export default function ContactPage() {
  const { t, lang } = useI18n();
  const [s, setS] = useState({ company_name: "TP Agency", email: "tp.agency@outlook.com", website: "tpagency.co", location: "Netherlands", phone: "+31 970 102 50680" });
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { api.get("/site-settings").then(r => setS(r.data)).catch(() => {}); }, []);

  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/leads/contact", { ...form, lang });
      setSent(true);
      setForm({ name: "", email: "", company: "", message: "" });
      toast.success(t.contact.success);
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-40 lg:pt-36 pb-8" data-testid="contact-page">
      <Seo k="contact" />
      <Band tone="light" className="pb-14">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-12">
            <div className="flex items-center justify-between gap-4 mb-3">
              <div className="eyebrow">{t.contact.eyebrow}</div>
              <PoseEagle
                src="/eagle-poses/eagle-shared-idle.png"
                successSrc="/eagle-poses/eagle-contact-success.png"
                success={sent}
                size={170}
                mobileSize={95}
                testid="contact-eagle"
                className="shrink-0"
              />
            </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter">{t.contact.title}</h1>
          <p className="text-slate-400 mt-4 text-lg">{t.contact.sub}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {/* CTA card */}
          <section className="glass-strong rounded-2xl p-8 flex flex-col" data-testid="contact-cta-card">
            <Logo size={44} />
            <div className="mt-8">
              <div className="eyebrow mb-3">{lang === "nl" ? "Snel aan de slag" : "Get started fast"}</div>
              <h2 className="font-display font-bold text-white text-2xl leading-tight">
                {lang === "nl"
                  ? "Plan een gratis analyse of stuur ons een bericht — wij regelen de opvolging."
                  : "Plan a free analysis or send us a message — we'll take it from there."}
              </h2>
            </div>

            <div className="mt-8 grid gap-3">
              <a
                href={GHL_CALENDAR_URL}
                {...externalLinkProps}
                data-testid="contact-book-demo-btn"
                className="btn-cta group inline-flex items-center justify-between gap-3 font-semibold px-6 py-4 rounded-xl"
              >
                <span className="inline-flex items-center gap-2.5">
                  <CalendarClock size={18} />
                  <span>{t.nav.bookDemo}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs opacity-80">
                  <ExternalLink size={13} />
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </a>

              <a
                href={GHL_FORM_URL}
                {...externalLinkProps}
                data-testid="contact-us-btn"
                className="group inline-flex items-center justify-between gap-3 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-white/30 text-white font-medium px-6 py-4 rounded-xl"
              >
                <span className="inline-flex items-center gap-2.5">
                  <MessageSquare size={18} />
                  <span>{t.contact.contactTitle}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
                  <ExternalLink size={13} />
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </a>
            </div>

            <div className="flex-1" />
            <p className="text-xs text-slate-500 mt-6 leading-relaxed">
              {lang === "nl"
                ? "Beide links openen in een nieuw tabblad. Uw contactgegevens worden veilig verwerkt."
                : "Both links open in a new tab. Your details are securely handled."}
            </p>
          </section>

          {/* Direct contact card */}
          <section className="glass rounded-2xl p-8" data-testid="contact-direct-card">
            <div className="eyebrow mb-6">{lang === "nl" ? "Direct contact" : "Direct contact"}</div>
            <div className="space-y-5">
              <a href={telHref(s.phone)} className="flex items-start gap-4 group" data-testid="contact-phone">
                <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-400/25 text-cyan-300 flex items-center justify-center shrink-0 group-hover:bg-cyan-500/20 transition-colors">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-[10px] tracking-widest text-slate-500 font-bold mb-0.5">{t.contact.info.phone.toUpperCase()}</div>
                  <div className="text-white font-mono font-semibold text-lg group-hover:text-cyan-300 transition-colors">{s.phone || "+31 970 102 50680"}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{lang === "nl" ? "Klik om te bellen" : "Tap to call"}</div>
                </div>
              </a>

              <a href={`mailto:${s.email}`} className="flex items-start gap-4 group" data-testid="contact-email">
                <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-400/25 text-blue-300 flex items-center justify-center shrink-0 group-hover:bg-blue-500/20 transition-colors">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="text-[10px] tracking-widest text-slate-500 font-bold mb-0.5">{t.contact.info.email.toUpperCase()}</div>
                  <div className="text-white font-medium group-hover:text-cyan-300 transition-colors break-all">{s.email}</div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-slate-300">
                  <Globe size={18} />
                </div>
                <div>
                  <div className="text-[10px] tracking-widest text-slate-500 font-bold mb-0.5">{t.contact.info.website.toUpperCase()}</div>
                  <div className="text-white">{s.website}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-slate-300">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="text-[10px] tracking-widest text-slate-500 font-bold mb-0.5">{t.contact.info.location.toUpperCase()}</div>
                  <div className="text-white">{s.location}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-slate-300">
                  <Building2 size={18} />
                </div>
                <div>
                  <div className="text-[10px] tracking-widest text-slate-500 font-bold mb-0.5">{t.contact.info.company.toUpperCase()}</div>
                  <div className="text-white">{s.company_name}</div>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-5 border-t border-white/10 flex items-start gap-3" data-testid="contact-caller-id-notice">
              <PhoneOutgoing size={15} className="text-slate-500 mt-0.5 shrink-0" />
              <p className="text-xs text-slate-500 leading-relaxed">
                <span className="text-slate-400 font-medium">{t.contact.callerIdTitle}</span>{" "}
                {t.contact.callerIdNote}{" "}
                <span className="font-mono text-slate-300" data-testid="contact-caller-id-number">{SITE_INFO.outboundCallerId}</span>.
              </p>
            </div>
          </section>
        </div>
        </div>
      </Band>

      {/* On-site message form */}
      <Band tone="dark" className="mt-2 py-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <section className="glass rounded-2xl p-8" data-testid="contact-form-card">
          <div className="max-w-3xl">
            <div className="eyebrow mb-2">{t.contact.messageTitle}</div>
            <p className="text-sm text-slate-400 mb-6">{t.contact.messageSub}</p>
            <form onSubmit={onSubmit} data-testid="contact-form" className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11px] tracking-widest text-slate-500 font-bold">{t.contact.name.toUpperCase()} *</span>
                <input
                  data-testid="contact-form-name"
                  type="text" required minLength={2} maxLength={120} value={form.name} onChange={onField("name")}
                  className="mt-1.5 w-full bg-white/5 border border-white/15 focus:border-cyan-400/60 rounded-lg px-4 py-3 text-white text-sm outline-none transition-colors"
                />
              </label>
              <label className="block">
                <span className="text-[11px] tracking-widest text-slate-500 font-bold">{t.contact.email.toUpperCase()} *</span>
                <input
                  data-testid="contact-form-email"
                  type="email" required value={form.email} onChange={onField("email")}
                  className="mt-1.5 w-full bg-white/5 border border-white/15 focus:border-cyan-400/60 rounded-lg px-4 py-3 text-white text-sm outline-none transition-colors"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-[11px] tracking-widest text-slate-500 font-bold">{t.contact.company.toUpperCase()}</span>
                <input
                  data-testid="contact-form-company"
                  type="text" maxLength={150} value={form.company} onChange={onField("company")}
                  className="mt-1.5 w-full bg-white/5 border border-white/15 focus:border-cyan-400/60 rounded-lg px-4 py-3 text-white text-sm outline-none transition-colors"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-[11px] tracking-widest text-slate-500 font-bold">{t.contact.message.toUpperCase()} *</span>
                <textarea
                  data-testid="contact-form-message"
                  required minLength={2} maxLength={2000} rows={5} value={form.message} onChange={onField("message")}
                  className="mt-1.5 w-full bg-white/5 border border-white/15 focus:border-cyan-400/60 rounded-lg px-4 py-3 text-white text-sm outline-none transition-colors resize-y"
                />
              </label>
              <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit"
                  className="btn-cta inline-flex items-center gap-2 font-semibold px-7 py-3.5 rounded-xl disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send size={16} />
                  {sending ? t.contact.sending : t.contact.submitContact}
                </button>
                {sent && (
                  <span className="text-sm text-cyan-300" data-testid="contact-form-success">{t.contact.success}</span>
                )}
              </div>
            </form>
          </div>
          </section>
        </div>
      </Band>
      <Band tone="light"><CTASection /></Band>
    </main>
  );
}
